import { useCallback, useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { Dialog, DialogContent, Grid, Paper, Typography, Tooltip } from '@material-ui/core'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import CustomTable from '../../components/CustomTable/CustomTable'
import { getIncidences } from '../../api/orderIncidences.service'
import { useDispatch, useSelector } from 'react-redux'
import { formatDate } from '../../common/utils'
import IncidenceDetail from './IncidenceDetail'
import { toggleModalFullscreen } from '../../redux/features/modalSlice'

const IncidencesView = () => {
  const [selectedIncidence, setSelectedIncidence] = useState({})
  const [isModalDetailOpen, setIsModalDetailOpen] = useState(false)
  const [state, setState] = useState({
    data: [],
    columns: [],
    loading: false,
    pages: 0,
    page: 0,
    pageSize: 15,
    sorted: null,
    filtered: [],
    createModal: true,
    hasChanges: true
  })
  const providerSelected = useSelector((state) => state.provider)
  const modal = useSelector((state) => state.modal)
  const dispatch = useDispatch()

  const [tableLoading, setTableLoading] = useState(true)

  const handleSetState = (values) => {
    const newState = { ...state, ...values, hasChanges: true }
    setState(newState)
  }

  const getData = useCallback(
    (nState) => {
      setTableLoading(true)
      const postObject = {
        providerId: providerSelected.id,
        ...(nState ? nState : state)
      }
      const data = JSON.stringify(postObject)
      getIncidences(data)
        .then(({ data }) => {
          setState({
            ...state,
            page: postObject.page,
            sorted: postObject.sorted,
            filtered: postObject.filtered,
            pageSize: postObject.pageSize,
            data: data.results,
            pages: data.pageCount,
            hasChanges: false
          })
          setTableLoading(false)
        })
        .catch(function (error) {
          console.error(error)
        })
    },
    [providerSelected.id, state]
  )

  const generateColumns = useCallback(() => {
    setState({
      ...state,
      columns: [
        {
          Header: 'Código',
          accessor: 'incidenceCode',
          filterable: true,
          sortable: true,
          idSort: 'id',
          Cell: (props) => <span style={{ float: 'right' }}>{props.value}</span>
        },
        {
          Header: 'Nº Pedido',
          accessor: 'orderNumber',
          filterable: true,
          sortable: true,
          Cell: (props) => (
            <span style={{ float: 'right' }}>{`${props.value}/${props.row.original.level}`}</span>
          )
        },
        {
          Header: 'Fecha recepción',
          accessor: 'receptionDate',
          filterable: true,
          sortable: true,
          idSort: 'startDate',
          Cell: (props) => <span style={{ float: 'right' }}>{formatDate(props.value)}</span>
        },
        {
          Header: 'Estado',
          accessor: 'state',
          filterable: true,
          sortable: true,
          Cell: (props) => <span style={{ float: 'right' }}>{props.value}</span>
        },
        {
          Header: '',
          accessor: 'details',
          sortable: false,
          Cell: (props) => (
            <span>
              <Tooltip title="Ver detalle de la incidencia">
                <VisibilityIcon
                  onClick={(e) => handleClickDetails(e, props.row.original.incidenceCode)}
                  height="25"
                  width="25"
                  style={{
                    cursor: 'pointer',
                    float: 'left',
                    marginLeft: '10px'
                  }}
                />
              </Tooltip>
            </span>
          ),
          width: 45
        }
      ]
    })
  }, [state])

  useEffect(() => {
    if (state.hasChanges && state.columns.length > 0) getData()
  }, [getData, state.hasChanges])

  useEffect(() => {
    if (Object.entries(state.columns).length === 0) generateColumns()
  }, [state.columns, generateColumns])

  const handleClickDetails = (e, incidenceCode) => {
    setSelectedIncidence({
      incidenceCode: incidenceCode
    })
    setIsModalDetailOpen(true)
  }

  const fetchData = (nState) => {
    getData(nState)
  }

  const handleCloseModalDetail = () => {
    getData()
    setIsModalDetailOpen(false)
  }

  const exitFullscreenModalDetail = useCallback(() => {
    if (!isModalDetailOpen && modal.showFullscreenOption) {
      dispatch(toggleModalFullscreen(false))
    }
  }, [isModalDetailOpen, modal.showFullscreenOption, dispatch])

  useEffect(() => {
    let timeout = setTimeout(exitFullscreenModalDetail, 500)
    return () => clearTimeout(timeout)
  }, [isModalDetailOpen, exitFullscreenModalDetail])

  return (
    <Paper style={{ padding: 16 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography color="secondary">INCIDENCIAS DE PEDIDOS</Typography>
        </Grid>
        <Grid item xs={12}>
          {state.loading ? (
            <p>
              <em>Cargando...</em>
            </p>
          ) : (
            <CustomTable
              {...state}
              numButtonColumns={0}
              tableLoading={tableLoading}
              fetchData={fetchData}
              handleSetState={handleSetState}
              filterProps={[
                { placeholder: 'Filtrar código...', id: 'id', value: '' },
                { placeholder: 'Filtrar Nº pedido...', id: 'orderNumber', value: '' },
                { width: 45, maxWidth: 45 },
                {
                  placeholder: 'Filtrar estado...',
                  id: 'state',
                  value: '',
                  options: [
                    { label: 'Abierta', value: 'Abierta' },
                    { label: 'Pendiente de Acción', value: 'Pendiente de Acción' },
                    { label: 'Pendiente de Motivo', value: 'Pendiente de Motivo' },
                    { label: 'Cerrada', value: 'Cerrada' },
                    { label: 'Todas', value: '' }
                  ]
                },
                { width: 45, maxWidth: 45 }
              ]}
              manualSortBy={true}
            />
          )}
        </Grid>
      </Grid>
      <Dialog
        open={isModalDetailOpen}
        onClose={handleCloseModalDetail}
        maxWidth={'md'}
        fullWidth={true}
        fullScreen={modal.isFullscreen}
      >
        <DialogContent>
          <IncidenceDetail
            incidenceCode={selectedIncidence.incidenceCode}
            onClose={handleCloseModalDetail}
          />
        </DialogContent>
      </Dialog>
    </Paper>
  )
}

export default withRouter(IncidencesView)
IncidencesView.displayName = 'Incidencias de Pedidos'
